import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { LineChartComponent } from "../../../../ChartComponents/LineChartFluent";
import CustomCard from "../../../../SharedComponents/CustomCard";
import { selectViewport } from "../../../../slices/UI.slice";
import { useAppSelector } from "../../../../store/hooks";
import { UsageTrendChartProps } from "./partnerInfluence.interfaces";


const formatMonthYear = (d: Date) => {
  return d.toLocaleDateString("en-US", { month: "short", year: "numeric" });
};

const UsageTrendChart = ({
  data,
  consumedColor = "#0078D4", // primary blue
  eliteColor = "#E3008C", // pink
  nonEliteColor = "#0E7C7B", // teal
  consumedLegend = "ConsumedSCU",
  eliteLegend = "ConsumedSCU Elite",
  nonEliteLegend = "ConsumedSCU NonElite",
  height = 400,
  cardTitle = "How are usage and licenses trending?",
  kustoLink,
  isLoading
}: UsageTrendChartProps) => {
  const widthCalc = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(500);
  const resizedWidth = useAppSelector((state) => selectViewport(state).offsetWidth);
  const chartWidth = Math.max(width - 10, 0);

  const handleResize = useCallback(setWidth, []);

  useEffect(() => {
    if (widthCalc.current) {
      handleResize(widthCalc.current.offsetWidth);
    }
  }, [handleResize, resizedWidth]);

  const chartData = useMemo(() => {
    const points = data.map((d) => {
      const date = new Date(d.snapshotDate);
      return { date, label: formatMonthYear(date), row: d };
    });
    return {
      chartTitle: cardTitle,
      lineChartData: [
        {
          legend: consumedLegend,
          color: consumedColor,
          data: points.map((p) => ({
            x: p.date,
            y: Number(p.row.consumedSCU),
            xAxisCalloutData: p.label
          }))
        },
        {
          legend: eliteLegend,
          color: eliteColor,
          data: points.map((p) => ({
            x: p.date,
            y: Number(p.row.consumedSCU_IsElite),
            xAxisCalloutData: p.label
          }))
        },
        {
          legend: nonEliteLegend,
          color: nonEliteColor,
          data: points.map((p) => ({
            x: p.date,
            y: Number(p.row.consumedSCU_NonElite),
            xAxisCalloutData: p.label
          }))
        }
      ]
    };
  }, [data, consumedColor, eliteColor, nonEliteColor, cardTitle, consumedLegend, eliteLegend, nonEliteLegend]);

  const cardContent = useMemo(
    () => <LineChartComponent data={chartData} height={height} width={chartWidth} allowResize={true} isLoading={isLoading} />,
    [chartData, height, chartWidth, isLoading]
  );

  const hasChartData = chartData.lineChartData.some((line) => line.data.length > 0);

  return (
    <div ref={widthCalc} style={{ width: "100%", minWidth: 0 }}>
      <CustomCard cardHeaderTitle={cardTitle} chartTitle={cardTitle} kustoLink={kustoLink} isLoading={isLoading}>
        {isLoading ? (
          cardContent
        ) : hasChartData ? (
          cardContent
        ) : (
          <div style={{ height: height, display: "flex", alignItems: "center", justifyContent: "center" }}>No data available</div>
        )}
      </CustomCard>
    </div>
  );
};

export default UsageTrendChart;
